/**
 * Admin-web access to standalone discussion threads, which live in
 * `discussions/{discussionId}` with a `messages` subcollection. A thread has two
 * axes: `kind` (dm | group) and `subjectType` (task | inspection | property |
 * none) — anchored threads get one doc per subject, a dm is keyed by its sorted
 * uid pair, a standalone group chat gets an auto id. Mirrors the mobile service
 * (mobile/services/firebase/discussionService.js) so both write the same shape
 * against the same security rules.
 *
 * Clients CREATE the parent and post immutable messages; all metadata
 * (participants — incl. invites/leaves —, hasOpenQuestion, questionTo,
 * lastMessage*) is maintained by the notifyOnDiscussionMessage Cloud Function.
 * `authorUid` MUST be the caller's uid (rules enforce it).
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const COL = "discussions";
const nowIso = () => new Date().toISOString();
const messagesCol = (id) => collection(db, COL, id, "messages");
const messageRef = (id, messageId) => doc(db, COL, id, "messages", messageId);

// Thread ids (must match the mobile service exactly). Anchored: "{type}__{id}".
// A dm's id is "dm__{pairKey}" where pairKey is the sorted uid pair.
export const subjectThreadId = (subjectType, subjectId) =>
  `${subjectType}__${subjectId}`;
export const dmPairKey = (a, b) => [a, b].sort().join("__");
export const dmThreadId = (a, b) => `dm__${dmPairKey(a, b)}`;

// Bounds the live thread read.
const MESSAGES_LIMIT = 30;

// Create a deterministic-id thread, tolerating a lost create race (a write to an
// existing doc is denied — re-read from the SERVER so a stale cached "missing"
// doesn't wrongly rethrow).
const createOrReadAt = async (id, data) => {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (snap.exists()) return { docId: id, ...snap.data() };
  try {
    await setDoc(ref, data);
  } catch (e) {
    const after = await getDocFromServer(ref);
    if (after.exists()) return { docId: id, ...after.data() };
    throw e;
  }
  return { docId: id, ...data };
};

/**
 * Get (or lazily create) the ANCHORED thread for a task/inspection/property.
 * `kind` is "group" (anchored threads are never dms). Returns {docId,...}.
 */
export const getOrCreateDiscussion = async ({
  subjectType,
  subjectId,
  title = "",
  propertyId = null,
  participants = [],
  creatorName,
}) => {
  if (!subjectType || !subjectId) throw new Error("Missing discussion subject");
  if (!["task", "inspection", "property"].includes(subjectType)) {
    throw new Error(`getOrCreateDiscussion: bad subjectType ${subjectType}`);
  }
  const uid = auth.currentUser?.uid || null;
  const seed = [...new Set([uid, ...participants].filter(Boolean))];
  return createOrReadAt(subjectThreadId(subjectType, subjectId), {
    kind: "group",
    subjectType,
    subjectId,
    propertyId: propertyId ?? null,
    title: title || "",
    participants: seed,
    createdBy: uid,
    createdByName: creatorName || auth.currentUser?.email || null,
    createdAt: nowIso(),
  });
};

/** Get (or lazily create) the 1:1 dm thread between the current user and `other`. */
export const getOrCreateDM = async ({ otherUid, title = "", creatorName }) => {
  const uid = auth.currentUser?.uid || null;
  if (!uid || !otherUid) throw new Error("getOrCreateDM: missing participant");
  if (uid === otherUid) throw new Error("getOrCreateDM: can't dm yourself");
  return createOrReadAt(dmThreadId(uid, otherUid), {
    kind: "dm",
    subjectType: "none",
    subjectId: dmPairKey(uid, otherUid),
    propertyId: null,
    title: title || "",
    participants: [uid, otherUid],
    createdBy: uid,
    createdByName: creatorName || auth.currentUser?.email || null,
    createdAt: nowIso(),
  });
};

/** Create a standalone GROUP chat (no subject) at an auto id. Returns {docId,...}. */
export const createGroup = async ({ title = "", participants = [], creatorName }) => {
  const uid = auth.currentUser?.uid || null;
  if (!uid) throw new Error("createGroup: missing creator");
  const seed = [...new Set([uid, ...participants].filter(Boolean))];
  const data = {
    kind: "group",
    subjectType: "none",
    subjectId: null,
    propertyId: null,
    title: (title || "").trim() || "New group",
    participants: seed,
    createdBy: uid,
    createdByName: creatorName || auth.currentUser?.email || null,
    createdAt: nowIso(),
  };
  const ref = await addDoc(collection(db, COL), data);
  return { docId: ref.id, ...data };
};

/**
 * Post a message. `type` "question" (non-dm threads) flags the thread and fans
 * out to admins. Pass `inviteUids` to pull people in, `questionTo` to direct a
 * question at someone.
 */
export const addDiscussionMessage = async (
  discussionId,
  { text, type = "note", authorName, imageUrls = [], inviteUids = [], questionTo = null } = {},
) => {
  if (!discussionId) throw new Error("Missing discussion id");
  const body = (text || "").trim();
  const images = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  const invite = Array.isArray(inviteUids) ? [...new Set(inviteUids.filter(Boolean))] : [];
  if (!body && images.length === 0) throw new Error("Add a message or a photo");
  const data = {
    text: body,
    type: type === "question" ? "question" : "note",
    authorUid: auth.currentUser?.uid || null,
    authorName: (authorName || "").trim() || auth.currentUser?.email || "Admin",
    imageUrls: images,
    createdAt: nowIso(),
  };
  if (invite.length) data.inviteUids = invite;
  if (type === "question" && questionTo) data.questionTo = questionTo;
  return addDoc(messagesCol(discussionId), data);
};

/**
 * Edit your own message's text (images preserved). Stamps `editedAt` so the UI can
 * show an "edited" marker. The deployed rules allow this only for the author and
 * only on the text/imageUrls/editedAt/deleted fields; if the edited message is the
 * thread's latest, the Cloud Function re-syncs the inbox preview. Mirrors the
 * mobile service (mobile/services/firebase/discussionService.js).
 */
export const editDiscussionMessage = async (
  discussionId,
  messageId,
  { text, imageUrls = [] } = {},
) => {
  if (!discussionId || !messageId) throw new Error("Missing message");
  const body = (text || "").trim();
  const images = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  if (!body && images.length === 0) {
    throw new Error("Message can't be empty — delete it instead");
  }
  return updateDoc(messageRef(discussionId, messageId), {
    text: body,
    imageUrls: images,
    editedAt: nowIso(),
  });
};

/**
 * Soft-delete your own message: mark `deleted` and blank the content, keeping the
 * doc for thread continuity. The UI renders it as "message deleted"; the Cloud
 * Function falls the inbox preview back to the latest remaining message.
 */
export const deleteDiscussionMessage = async (discussionId, messageId) => {
  if (!discussionId || !messageId) throw new Error("Missing message");
  return updateDoc(messageRef(discussionId, messageId), {
    deleted: true,
    text: "",
    imageUrls: [],
    editedAt: nowIso(),
  });
};

/** Pull people into a thread (posts a system/join marker carrying inviteUids). */
export const inviteToDiscussion = async (discussionId, { uids = [], authorName } = {}) => {
  if (!discussionId) throw new Error("Missing discussion id");
  const invite = [...new Set((uids || []).filter(Boolean))];
  if (!invite.length) return null;
  return addDoc(messagesCol(discussionId), {
    text: "",
    type: "system",
    event: "join",
    inviteUids: invite,
    authorUid: auth.currentUser?.uid || null,
    authorName: (authorName || "").trim() || auth.currentUser?.email || "Admin",
    imageUrls: [],
    createdAt: nowIso(),
  });
};

/** Leave a thread (posts a self-authored system/leave marker). */
export const leaveDiscussion = async (discussionId, { authorName } = {}) => {
  if (!discussionId) throw new Error("Missing discussion id");
  return addDoc(messagesCol(discussionId), {
    text: "",
    type: "system",
    event: "leave",
    authorUid: auth.currentUser?.uid || null,
    authorName: (authorName || "").trim() || auth.currentUser?.email || "Admin",
    imageUrls: [],
    createdAt: nowIso(),
  });
};

/** Realtime thread — the most recent MESSAGES_LIMIT, oldest-first. */
export const subscribeDiscussionMessages = (discussionId, onChange) => {
  if (!discussionId) return () => {};
  const q = query(
    messagesCol(discussionId),
    orderBy("createdAt", "desc"),
    limit(MESSAGES_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) =>
      onChange(snap.docs.map((d) => ({ _id: d.id, ...d.data() })).reverse()),
    (err) => {
      console.error("subscribeDiscussionMessages failed", err);
      onChange([]);
    },
  );
};

/**
 * Realtime subscription to a single thread doc — so a drawer kept open while the
 * Cloud Function reconciles membership (invites/leaves, assignee changes) sees the
 * live `participants`/`title` rather than the stale create-time snapshot. Returns
 * {docId,...} or null. Unsubscribe with the returned function.
 */
export const subscribeDiscussion = (discussionId, onChange) => {
  if (!discussionId) {
    onChange(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, COL, discussionId),
    (snap) => onChange(snap.exists() ? { docId: snap.id, ...snap.data() } : null),
    (err) => {
      console.error("subscribeDiscussion failed", err);
      onChange(null);
    },
  );
};

/**
 * Realtime list of ALL the user's threads (dm + task + inspection they're a
 * participant in), most-recently-active first — drives the admin Messages page.
 * Needs the composite index on (participants array-contains, lastMessageAt desc).
 */
export const subscribeMyThreads = (uid, onChange) => {
  if (!uid) return () => {};
  const q = query(
    collection(db, COL),
    where("participants", "array-contains", uid),
    orderBy("lastMessageAt", "desc"),
    limit(50),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ docId: d.id, ...d.data() }))),
    (err) => {
      console.error("subscribeMyThreads failed", err);
      onChange([]);
    },
  );
};

// Cached briefly — the directory changes rarely, so re-opening drawers in a
// session shouldn't re-read the whole collection each time.
let _dirCache = null;
let _dirCacheAt = 0;
const DIR_TTL_MS = 60 * 1000;

/** The public people directory (uid → {name, image}) — for message avatars. */
export const getUserDirectory = async (force = false) => {
  if (!force && _dirCache && Date.now() - _dirCacheAt < DIR_TTL_MS) {
    return _dirCache;
  }
  const snap = await getDocs(collection(db, "user_profiles"));
  _dirCache = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
  _dirCacheAt = Date.now();
  return _dirCache;
};

// ── Read state ── (mirrors the mobile service; discussion_reads/{uid} holds a
// map of { threadId: lastReadAtIso }; a thread is unread when its last message is
// newer than my marker and I didn't send it.)
const READS = "discussion_reads";

/**
 * Stamp how far I've read a thread. Pass `upTo` = the last seen message's
 * createdAt so the marker compares apples-to-apples with lastMessageAt (avoids
 * client-clock skew); falls back to `now` when no message is loaded yet.
 */
export const markThreadRead = async (uid, threadId, upTo) => {
  if (!uid || !threadId) return;
  try {
    await setDoc(doc(db, READS, uid), { [threadId]: upTo || nowIso() }, { merge: true });
  } catch (e) {
    console.warn("markThreadRead failed", e?.message);
  }
};

/** Realtime subscription to my read-state map ({ threadId: isoTimestamp }). */
export const subscribeMyThreadReads = (uid, onChange) => {
  if (!uid) {
    onChange({});
    return () => {};
  }
  return onSnapshot(
    doc(db, READS, uid),
    (snap) => onChange(snap.exists() ? snap.data() : {}),
    (err) => {
      console.error("subscribeMyThreadReads failed", err);
      onChange({});
    },
  );
};

/** Whether `thread` has unread activity for `uid`, given the read-state map. */
export const isThreadUnread = (thread, reads, uid) => {
  const at = thread?.lastMessageAt;
  if (!at || thread?.lastMessageBy === uid) return false;
  const readAt = reads?.[thread.docId];
  return !readAt || at > readAt;
};

/**
 * Live list of my unread thread ids — combines the my-threads + read-state
 * listeners. Subscribed ONCE at the layout level and stored in redux; the header
 * badge and the table-row dots read it from there (no per-component listeners).
 */
export const subscribeUnreadThreadIds = (uid, onIds) => {
  if (!uid) {
    onIds([]);
    return () => {};
  }
  let threads = [];
  let reads = {};
  const recompute = () =>
    onIds(threads.filter((t) => isThreadUnread(t, reads, uid)).map((t) => t.docId));
  const u1 = subscribeMyThreads(uid, (l) => {
    threads = l;
    recompute();
  });
  const u2 = subscribeMyThreadReads(uid, (m) => {
    reads = m;
    recompute();
  });
  return () => {
    u1 && u1();
    u2 && u2();
  };
};
